import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ShoppingBag } from "lucide-react";
import { useBucket } from "../state/bucket-context";

const NAVY = "#110E39";
const GOLD = "#E5B265";

interface BucketIconButtonProps {
  className?: string;
  /** Render nothing while the bucket is empty. */
  hideWhenEmpty?: boolean;
}

/**
 * Header bag icon with a gold badge showing the live bucket count: the count
 * reads as a brand mark, not an alarm. Clicking navigates to /$lang/bucket.
 */
export function BucketIconButton({ className = "", hideWhenEmpty = false }: BucketIconButtonProps) {
  const { t } = useTranslation();
  const { count } = useBucket();
  const { lang } = useParams({ strict: false }) as { lang?: string };
  if (hideWhenEmpty && count === 0) return null;
  const langPrefix = lang ? `/${lang}` : "";
  return (
    <Link
      to={`${langPrefix}/bucket` as never}
      className={`mj-press relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(17,14,57,0.055)] hover:bg-[rgba(17,14,57,0.09)] ${className}`}
      style={{ color: NAVY }}
      aria-label={t("bucket.open_bucket")}
    >
      <ShoppingBag size={18} strokeWidth={2.2} aria-hidden />
      {count > 0 && (
        <span
          aria-hidden="true"
          className="absolute -top-0.5 -end-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none tabular-nums"
          style={{ background: GOLD, color: NAVY, boxShadow: "0 0 0 2px #fff" }}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}
